import { supabase } from './supabase';
import { Post, User } from '../types';

export const getUserProfile = async (userId: string): Promise<User | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;

    if (!data) return null;

    // Get post count
    const { count: postCount } = await supabase
      .from('posts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    // Transform database profile to User type
    return {
      id: data.id,
      username: data.username,
      displayName: data.full_name || data.username,
      profileImage: data.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: data.bio || '',
      followerCount: 0, // These would come from a followers table in a real app
      followingCount: 0,
      postCount: postCount || 0,
    };
  } catch (err) {
    console.error('Error loading user profile:', err);
    return null;
  }
};

export const getUserPosts = async (userId: string, limit: number = 12, offset: number = 0): Promise<Post[]> => {
  try {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        profiles:user_id (
          id,
          username,
          full_name,
          avatar_url
        ),
        post_media (
          id,
          media_url,
          media_type,
          order_index,
          width,
          height
        ),
        post_hashtags (
          id,
          hashtag
        ),
        post_user_tags (
          id,
          tagged_user_id,
          x_position,
          y_position,
          profiles:tagged_user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        ),
        post_likes (
          id,
          user_id
        ),
        post_comments (
          id,
          user_id,
          content,
          created_at,
          profiles:user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Database query error:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform the data to match our Post interface
    return data.map(post => {
      const profile = post.profiles || {
        id: post.user_id,
        username: 'unknown',
        full_name: 'Unknown User',
        avatar_url: 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300'
      };

      return {
        id: post.id,
        user_id: post.user_id,
        user: {
          id: profile.id,
          username: profile.username,
          displayName: profile.full_name || profile.username,
          profileImage: profile.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
          bio: '',
          followerCount: 0,
          followingCount: 0,
          postCount: 0,
        },
        caption: post.caption || '',
        location: post.location,
        created_at: post.created_at,
        updated_at: post.updated_at,
        media: (post.post_media || [])
          .filter((media: any) => !media.media_url.includes('example.com'))
          .sort((a: any, b: any) => a.order_index - b.order_index),
        hashtags: post.post_hashtags || [],
        user_tags: (post.post_user_tags || []).map((tag: any) => ({
          ...tag,
          tagged_user: tag.profiles ? {
            id: tag.profiles.id,
            username: tag.profiles.username,
            displayName: tag.profiles.full_name || tag.profiles.username,
            profileImage: tag.profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
            bio: '',
            followerCount: 0,
            followingCount: 0,
            postCount: 0,
          } : null
        })),
        likes: post.post_likes || [],
        comments: (post.post_comments || []).map((comment: any) => ({
          ...comment,
          user: comment.profiles ? {
            id: comment.profiles.id,
            username: comment.profiles.username,
            displayName: comment.profiles.full_name || comment.profiles.username,
            profileImage: comment.profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
            bio: '',
            followerCount: 0,
            followingCount: 0,
            postCount: 0,
          } : null
        })),
        like_count: (post.post_likes || []).length,
        comment_count: (post.post_comments || []).length,
        is_liked: (post.post_likes || []).some((like: any) => like.user_id === userId),
        is_saved: false,
      };
    });
  } catch (err) {
    console.error('Error in getUserPosts:', err);
    return [];
  }
};